import { FeaturePageHeader } from 'components/shell/FeaturePageShell';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Chip,
  IconButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import LastPageIcon from '@material-ui/icons/LastPage';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '../../../util/LocalizedMomentUtils';
import moment from 'moment';
import { useIntl } from 'react-intl';
import SafeProductImage from '../../../components/kea/SafeProductImage';
import LoadingState from '../../../components/kea/LoadingState';
import UiSegmentedControl from '../../../components/kea/SegmentedControl';

const DEFAULT_COPY = Object.freeze({
  ACTIVE: 'Live',
  ARCHIVED: 'Archived',
  CREATE_SEARCH_RULE: 'Create search rule',
  DISABLED: 'Disabled',
  DRAFT: 'Draft',
  EMPTY_BODY: 'Create the first rule to shape what shoppers see.',
  EMPTY_HEADING: 'Nothing here yet',
  HIGHER: 'Higher',
  LOAD_ERROR: 'This view could not be loaded. Your work is safe.',
  LOWER: 'Lower',
  NORMAL: 'Natural',
  RETRY: 'Try again',
  SCHEDULED: 'Scheduled',
  SEPARATOR: '·',
  SUGGESTED: 'Suggested',
  TIMES: '×',
});

export const statusCopy = (status, copy = DEFAULT_COPY) => ({
  active: copy.ACTIVE,
  draft: copy.DRAFT,
  suggested: copy.SUGGESTED || DEFAULT_COPY.SUGGESTED,
  scheduled: copy.SCHEDULED || DEFAULT_COPY.SCHEDULED,
  disabled: copy.DISABLED,
  archived: copy.ARCHIVED || DEFAULT_COPY.ARCHIVED,
}[status] || status);

export const StatusChip = ({ status, copy }) => (
  <Chip
    size="small"
    className={`mp-status-chip mp-status-chip--${status}`}
    label={statusCopy(status, copy)}
    icon={<span className="mp-status-dot" aria-hidden="true" />}
  />
);

StatusChip.propTypes = { status: PropTypes.string.isRequired, copy: PropTypes.object };
StatusChip.defaultProps = { copy: DEFAULT_COPY };

export const PageHeader = ({
  eyebrow, heading, body, primaryLabel, primaryIcon, primaryDisabled, onPrimary, secondary,
}) => (
  <FeaturePageHeader
    title={heading}
    description={body}
    eyebrow={eyebrow}
    actions={(secondary || primaryLabel) && <>
      {secondary}
      {primaryLabel && (
        <Button
          variant="contained"
          color="primary"
          onClick={onPrimary}
          disabled={primaryDisabled}
          startIcon={primaryIcon || <AddIcon />}
        >
          {primaryLabel}
        </Button>
      )}
    </>}
  />
);

PageHeader.propTypes = {
  eyebrow: PropTypes.node,
  heading: PropTypes.node.isRequired,
  body: PropTypes.node,
  primaryLabel: PropTypes.node,
  primaryIcon: PropTypes.node,
  primaryDisabled: PropTypes.bool,
  onPrimary: PropTypes.func,
  secondary: PropTypes.node,
};

export const InlineNotice = ({ children, onDismiss, dismissLabel, tone }) => (
  <div
    className={`mp-inline-notice mp-inline-notice--${tone}`}
    role={tone === 'error' ? 'alert' : 'status'}
  >
    <span>{children}</span>
    {onDismiss && (
      <button type="button" className="mp-inline-notice-dismiss" aria-label={dismissLabel} onClick={onDismiss}>
        <CloseIcon />
      </button>
    )}
  </div>
);

InlineNotice.propTypes = {
  children: PropTypes.node.isRequired,
  onDismiss: PropTypes.func,
  dismissLabel: PropTypes.string,
  tone: PropTypes.oneOf(['error', 'success', 'neutral']),
};
InlineNotice.defaultProps = { onDismiss: null, dismissLabel: 'Dismiss', tone: 'error' };

PageHeader.defaultProps = {
  eyebrow: null,
  body: null,
  primaryLabel: null,
  primaryIcon: null,
  primaryDisabled: false,
  onPrimary: null,
  secondary: null,
};

export const StateBoundary = ({
  state, onReady, children, copy, emptyHeading, emptyBody, emptyActionLabel, onEmptyAction,
}) => {
  if (state === 'loading') {
    return (
      <div className="mp-state-panel mp-state-panel--loading">
        <LoadingState variant="compact" testId="merchandising-loading" />
      </div>
    );
  }
  if (state === 'empty') {
    return (
      <div className="mp-state-panel">
        <span className="mp-state-icon"><InboxOutlinedIcon /></span>
        <h2>{emptyHeading || copy.EMPTY_HEADING}</h2>
        <p>{emptyBody || copy.EMPTY_BODY}</p>
        <Button variant="contained" color="primary" onClick={onEmptyAction || onReady} startIcon={<AddIcon />}>
          {emptyActionLabel || copy.CREATE_SEARCH_RULE}
        </Button>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="mp-state-panel" role="alert">
        <span className="mp-state-icon mp-state-icon--error"><ErrorOutlineIcon /></span>
        <h2>{copy.LOAD_ERROR}</h2>
        <Button variant="outlined" onClick={onReady} startIcon={<RefreshIcon />}>
          {copy.RETRY}
        </Button>
      </div>
    );
  }
  return children;
};

StateBoundary.propTypes = {
  state: PropTypes.string.isRequired,
  onReady: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  copy: PropTypes.object,
  emptyHeading: PropTypes.node,
  emptyBody: PropTypes.node,
  emptyActionLabel: PropTypes.node,
  onEmptyAction: PropTypes.func,
};
StateBoundary.defaultProps = {
  copy: DEFAULT_COPY,
  emptyHeading: null,
  emptyBody: null,
  emptyActionLabel: null,
  onEmptyAction: null,
};

export const SearchInput = ({ value, onChange, placeholder, label }) => (
  <label className="mp-search-input">
    <span className="sr-only">{label || placeholder}</span>
    <SearchIcon aria-hidden="true" />
    <input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
    />
  </label>
);

SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  label: PropTypes.string,
};

SearchInput.defaultProps = { label: null };

export const SelectionCheckbox = ({ checked, disabled, indeterminate, label, onChange }) => (
  <Checkbox
    className="mp-selection-checkbox"
    size="small"
    color="primary"
    checked={checked}
    disabled={disabled}
    indeterminate={indeterminate}
    onChange={onChange}
    inputProps={{ 'aria-label': label }}
  />
);

SelectionCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

SelectionCheckbox.defaultProps = { disabled: false, indeterminate: false };

export const SegmentedControl = ({ value, options, onChange, label }) => (
  <UiSegmentedControl
    className="mp-segmented"
    value={value}
    options={options.map((option) => ({
      value: option.VALUE,
      label: option.LABEL,
    }))}
    onChange={onChange}
    label={label}
  />
);

SegmentedControl.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    VALUE: PropTypes.string.isRequired,
    LABEL: PropTypes.node.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

export const productImageSource = (product = {}) => (
  product.IMAGE
  || product.image_url
  || product.thumbnail_url
  || product.thumbnail
  || product.picture
  || product.image
  || ''
);

export const ProductImage = ({ product, size, fit }) => (
  <div className={`mp-product-image mp-product-image--${size}`}>
    <SafeProductImage
      src={productImageSource(product)}
      alt=""
      style={{
        width: '100%',
        height: '100%',
        objectFit: fit || (size === 'card' ? 'contain' : 'cover'),
      }}
    />
  </div>
);

ProductImage.propTypes = {
  product: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['card', 'thumb']),
  fit: PropTypes.oneOf(['contain', 'cover']),
};

ProductImage.defaultProps = { size: 'card', fit: null };

export const PriorityLabel = ({ factor, copy }) => {
  const numeric = Number(factor);
  const kind = numeric > 1 ? 'higher' : (numeric < 1 ? 'lower' : 'normal');
  const direction = kind === 'higher' ? copy.HIGHER : (kind === 'lower' ? copy.LOWER : copy.NORMAL);
  return (
    <span className={`mp-priority-label mp-priority-label--${kind}`}>
      <span aria-hidden="true" className="mp-priority-glyph">
        {kind === 'higher' ? '↑' : (kind === 'lower' ? '↓' : '—')}
      </span>
      <span>{direction}{kind === 'normal' ? '' : ` ${copy.SEPARATOR} ${numeric}${copy.TIMES}`}</span>
    </span>
  );
};

PriorityLabel.propTypes = { factor: PropTypes.number.isRequired, copy: PropTypes.object };
PriorityLabel.defaultProps = { copy: DEFAULT_COPY };

const visiblePageNumbers = (page, pageCount) => {
  if (pageCount <= 3) return Array.from({ length: pageCount }, (_, index) => index + 1);
  if (page <= 2) return [1, 2, 3];
  if (page >= pageCount - 1) return [pageCount - 2, pageCount - 1, pageCount];
  return [page - 1, page, page + 1];
};

export const WorkbenchPagination = ({
  page, pageCount, onChange, firstLabel, previousLabel, nextLabel, lastLabel,
}) => {
  const safePageCount = Math.max(1, Number(pageCount) || 1);
  const go = (next) => onChange(Math.max(1, Math.min(safePageCount, next)));
  return (
    <nav className="mp-workbench-pagination" aria-label={`${page} / ${safePageCount}`}>
      <IconButton aria-label={firstLabel} disabled={page <= 1} onClick={() => go(1)}><FirstPageIcon /></IconButton>
      <IconButton aria-label={previousLabel} disabled={page <= 1} onClick={() => go(page - 1)}><ChevronLeftIcon /></IconButton>
      <div className="mp-page-numbers">
        {visiblePageNumbers(page, safePageCount).map((number) => (
          <button
            key={number}
            type="button"
            className={number === page ? 'is-active' : ''}
            aria-current={number === page ? 'page' : undefined}
            aria-label={`${number}`}
            onClick={() => go(number)}
          >
            {number}
          </button>
        ))}
      </div>
      <IconButton aria-label={nextLabel} disabled={page >= safePageCount} onClick={() => go(page + 1)}><ChevronRightIcon /></IconButton>
      <IconButton aria-label={lastLabel} disabled={page >= safePageCount} onClick={() => go(safePageCount)}><LastPageIcon /></IconButton>
    </nav>
  );
};

WorkbenchPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  firstLabel: PropTypes.string.isRequired,
  previousLabel: PropTypes.string.isRequired,
  nextLabel: PropTypes.string.isRequired,
  lastLabel: PropTypes.string.isRequired,
};

export const formatLocalDateTime = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hourCycle: 'h23',
  }).format(date);
};

const toIso = (value) => {
  if (!value) return null;
  if (typeof value.isValid === 'function' && !value.isValid()) return null;
  return typeof value.toISOString === 'function' ? value.toISOString() : new Date(value).toISOString();
};

export const ScheduleCard = ({
  enabled, validFrom, validTo, onEnabledChange, onChange, copy, disabled,
}) => {
  const intl = useIntl();
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const format = /^en-US|^en$/i.test(intl.locale || '')
    ? 'MM/DD/YYYY HH:mm'
    : 'DD/MM/YYYY HH:mm';
  return (
    <section className="mp-setting-card mp-schedule-card" aria-label={copy.SCHEDULE}>
      <button
        type="button"
        className="mp-switch-row"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={() => onEnabledChange(!enabled)}
      >
        <span><strong>{copy.SCHEDULE}</strong><small>{enabled ? copy.SCHEDULE_ENABLED : copy.NO_SCHEDULE}</small></span>
        <i aria-hidden="true"><b /></i>
      </button>
      {enabled && (
        <MuiPickersUtilsProvider utils={MomentUtils} locale={intl.locale}>
          <div className="mp-date-grid">
          <KeyboardDateTimePicker
            id="merchandising-schedule-start"
            className="mp-date-picker"
            label={copy.STARTS}
            inputVariant="outlined"
            fullWidth
            open={startOpen}
            onOpen={() => setStartOpen(true)}
            onClose={() => setStartOpen(false)}
            onClick={() => setStartOpen(true)}
            inputProps={{ 'aria-label': copy.STARTS }}
            value={validFrom ? moment(validFrom) : null}
            onChange={(value) => onChange('validFrom', toIso(value))}
            format={format}
            ampm={false}
            mask="__/__/____ __:__"
            clearable
            disabled={disabled}
            clearLabel={copy.CLEAR}
            cancelLabel={copy.CANCEL}
            okLabel={copy.DONE}
            DialogProps={{ className: 'mp-date-picker-dialog' }}
          />
          <KeyboardDateTimePicker
            id="merchandising-schedule-end"
            className="mp-date-picker"
            label={copy.ENDS}
            inputVariant="outlined"
            fullWidth
            open={endOpen}
            onOpen={() => setEndOpen(true)}
            onClose={() => setEndOpen(false)}
            onClick={() => setEndOpen(true)}
            inputProps={{ 'aria-label': copy.ENDS }}
            value={validTo ? moment(validTo) : null}
            onChange={(value) => onChange('validTo', toIso(value))}
            format={format}
            ampm={false}
            mask="__/__/____ __:__"
            clearable
            disabled={disabled}
            clearLabel={copy.CLEAR}
            cancelLabel={copy.CANCEL}
            okLabel={copy.DONE}
            DialogProps={{ className: 'mp-date-picker-dialog' }}
          />
          </div>
        </MuiPickersUtilsProvider>
      )}
    </section>
  );
};

ScheduleCard.propTypes = {
  enabled: PropTypes.bool.isRequired,
  validFrom: PropTypes.string,
  validTo: PropTypes.string,
  onEnabledChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  copy: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

ScheduleCard.defaultProps = { validFrom: null, validTo: null, disabled: false };
